import { lazy, Suspense } from "react";
import {
  createBrowserRouter,
  createHashRouter,
  Navigate,
  type RouteObject,
} from "react-router-dom";
import { AppLayout } from "@/layouts/AppLayout";
import { RouteError } from "@/components/RouteError";
import { PageFallback } from "@/components/layout/PageFallback";
import { isTauri } from "@/utils";

/*
 * Pages are code-split: each becomes its own chunk that loads on first visit,
 * keeping the initial bundle small. `Suspense` shows a lightweight fallback
 * during the fetch.
 */
const ChatPage = lazy(() => import("@/pages/ChatPage"));
const ConversationComparePage = lazy(() => import("@/pages/ConversationComparePage"));
const NotesPage = lazy(() => import("@/pages/NotesPage"));
const PromptLibraryPage = lazy(() => import("@/pages/PromptLibraryPage"));
const ProjectsPage = lazy(() => import("@/pages/ProjectsPage"));
const ProjectDetailPage = lazy(() => import("@/pages/ProjectDetailPage"));
const TasksPage = lazy(() => import("@/pages/TasksPage"));
const FilesPage = lazy(() => import("@/pages/FilesPage"));
const KnowledgePage = lazy(() => import("@/pages/KnowledgePage"));
const MemoryPage = lazy(() => import("@/pages/MemoryPage"));
const ModelsPage = lazy(() => import("@/pages/ModelsPage"));
const SearchPage = lazy(() => import("@/pages/SearchPage"));
const ClipboardPage = lazy(() => import("@/pages/ClipboardPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));

/** Wrap a lazy page in the shared Suspense fallback. */
function page(element: React.ReactNode) {
  return <Suspense fallback={<PageFallback />}>{element}</Suspense>;
}

const routes: RouteObject[] = [
  {
    path: "/",
    element: <AppLayout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to="/chat" replace /> },
      { path: "chat", element: page(<ChatPage />) },
      { path: "compare", element: page(<ConversationComparePage />) },
      { path: "notes", element: page(<NotesPage />) },
      { path: "prompts", element: page(<PromptLibraryPage />) },
      { path: "projects", element: page(<ProjectsPage />) },
      { path: "projects/:id", element: page(<ProjectDetailPage />) },
      { path: "tasks", element: page(<TasksPage />) },
      { path: "files", element: page(<FilesPage />) },
      { path: "knowledge", element: page(<KnowledgePage />) },
      { path: "memory", element: page(<MemoryPage />) },
      { path: "models", element: page(<ModelsPage />) },
      { path: "search", element: page(<SearchPage />) },
      { path: "clipboard", element: page(<ClipboardPage />) },
      { path: "settings", element: page(<SettingsPage />) },
      { path: "about", element: page(<AboutPage />) },
      { path: "*", element: <Navigate to="/chat" replace /> },
    ],
  },
];

/*
 * In the Tauri shell the app is served from the `file://` protocol, where a
 * hash router is the reliable choice; in a browser dev context we can use the
 * history API. Selecting here keeps the rest of the app router-agnostic.
 */
export const router = isTauri()
  ? createHashRouter(routes)
  : createBrowserRouter(routes);
